/**
 * PaymentsPage
 * Page for viewing and exporting payment data for approved pots.
 * Pattern: Page as Orchestrator using PageContentArea + PageHeader + PageToolbar
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DownloadIcon,
  PageHeader,
  Spinner,
  Text,
  Tooltip,
  VisibilityIcon,
  VisibilityOffIcon,
} from '@billda/design-system';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { PaymentsListView } from '../components/PaymentsListView';
import { PaymentDetailPanelContent } from '../components/panel/PaymentDetailPanelContent';
import { PaymentGroupingMenu } from '../components/PaymentGroupingMenu';
import { PageSnackbar } from '../components/PageSnackbar';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { PeriodSelector } from '../components/PeriodSelector';
import { usePotPeriodState } from '../hooks/usePotPeriodState';
import { usePeriodNavigation } from '../hooks/usePeriodNavigation';
import { useGetPotPeriods } from '../hooks/useGetPotPeriods';
import { usePayments, useDownloadPaymentsCsv } from '../hooks/usePayments';
import { usePaymentsUIStore } from '../stores/paymentsUIStore';
import { aggregatePaymentsByWorker } from '../utils/groupPaymentsByWorker';
import { formatCurrency } from '../../../shared/utils/currency';
import type { PaymentRowDto } from '../api/paymentsApi';

export default function PaymentsPage() {
  const [yearsToLoad, setYearsToLoad] = useState(3);

  // Snackbar state for export feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Side panel state
  const [selectedPayment, setSelectedPayment] = useState<PaymentRowDto | null>(
    null
  );

  // 1. URL state — period selection
  const { selectedPeriodId: urlPeriodId, setPeriodId } = usePotPeriodState();

  // 1a. Local period state for immediate updates (URL updates are async)
  const [localPeriodId, setLocalPeriodId] = useState<string | null>(null);
  const selectedPeriodId = localPeriodId ?? urlPeriodId;

  useEffect(() => {
    if (urlPeriodId && urlPeriodId !== localPeriodId) {
      setLocalPeriodId(urlPeriodId);
    }
  }, [urlPeriodId, localPeriodId]);

  const handlePeriodChange = useCallback(
    (periodId: string) => {
      setLocalPeriodId(periodId);
      setPeriodId(periodId);
    },
    [setPeriodId]
  );

  // 2. Show/hide bank details state
  const [showBankDetails, setShowBankDetails] = useState(true);

  // 3. Fetch period data
  const { data: periodData } = useGetPotPeriods({ yearsToLoad });

  const handleLoadMoreYears = useCallback(() => {
    setYearsToLoad(prev => prev + 3);
  }, []);

  // 4. Period navigation hook
  const {
    effectivePeriodId,
    validatedPeriodId,
    handlePrev,
    handleNext,
    canGoPrev,
    canGoNext,
    periodsByYear,
    selectedPeriodLabel,
    selectedPeriodShortLabel,
  } = usePeriodNavigation({
    periodData,
    selectedPeriodId,
    onPeriodChange: handlePeriodChange,
  });

  // 5. Fetch payments data — use validatedPeriodId to avoid 404s with stale IDs
  const { payments, isLoading, error } = usePayments(validatedPeriodId);

  // 5a. Grouping mode
  const groupingMode = usePaymentsUIStore(state => state.groupingMode);

  // 6. Download CSV mutation
  const { mutate: downloadCsv, isPending: isDownloading } =
    useDownloadPaymentsCsv();

  // 7. Computed values
  const hasPayments = payments.length > 0;
  const totalAmount = useMemo(
    () => payments.reduce((sum, p) => sum + p.amountDue, 0),
    [payments]
  );
  const displayCount = useMemo(
    () =>
      groupingMode === 'none'
        ? aggregatePaymentsByWorker(payments).length
        : payments.length,
    [payments, groupingMode]
  );

  // 8. Handlers
  const handleExportClick = useCallback(() => {
    if (!selectedPeriodId) return;
    downloadCsv(selectedPeriodId, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: 'CSV downloaded successfully',
          severity: 'success',
        });
      },
      onError: () => {
        setSnackbar({
          open: true,
          message: 'Failed to download CSV',
          severity: 'error',
        });
      },
    });
  }, [selectedPeriodId, downloadCsv]);

  const handleToggleBankDetails = useCallback(() => {
    setShowBankDetails(prev => !prev);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const handlePaymentClick = useCallback((payment: PaymentRowDto) => {
    setSelectedPayment(payment);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedPayment(null);
  }, []);

  // 9. Reset store on unmount
  useEffect(() => {
    return () => {
      usePaymentsUIStore.getState().reset();
    };
  }, []);

  // 10. Panel content
  const isPanelOpen = selectedPayment !== null;

  // When in 'none' mode, find the raw rows that were aggregated into the selected payment
  const breakdownRows = useMemo(() => {
    if (!selectedPayment || groupingMode !== 'none') return undefined;
    const key = `${selectedPayment.personName}|${selectedPayment.accountNumber ?? ''}|${selectedPayment.sortCode ?? ''}`;
    return payments.filter(
      p =>
        `${p.personName}|${p.accountNumber ?? ''}|${p.sortCode ?? ''}` === key
    );
  }, [selectedPayment, payments, groupingMode]);

  const panelContent = useMemo(() => {
    if (!selectedPayment) return undefined;
    return (
      <>
        <ResizableHandle storageKey="payment-details" />
        <SidePanel
          open
          onClose={handleClosePanel}
          title="Payment Details"
          testId="payment-details-panel"
          storageKey="payment-details"
        >
          <PaymentDetailPanelContent
            payment={selectedPayment}
            breakdownRows={breakdownRows}
          />
        </SidePanel>
      </>
    );
  }, [selectedPayment, handleClosePanel, breakdownRows]);

  // 11. Header title with count and total
  const headerTitle = useMemo(() => {
    if (!hasPayments) return `Payments (${displayCount})`;
    return (
      <>
        Payments ({displayCount}){' '}
        <Text as="span" variant="inherit" color="secondary">
          Total: {formatCurrency(totalAmount)}
        </Text>
      </>
    );
  }, [displayCount, hasPayments, totalAmount]);

  return (
    <PageContentArea isPanelOpen={isPanelOpen} panel={panelContent}>
      <PageHeader
        title={headerTitle}
        actions={
          <>
            <PageHeaderAction
              label="Bank Details"
              onClick={handleToggleBankDetails}
              icon={
                showBankDetails ? <VisibilityOffIcon /> : <VisibilityIcon />
              }
              disabled={!hasPayments}
              data-testid="toggle-bank-details-button"
            />
            <Tooltip
              content={
                !hasPayments ? 'No approved or paid pots in this period' : ''
              }
              side="bottom"
            >
              <span>
                <PageHeaderAction
                  label="Export CSV"
                  onClick={handleExportClick}
                  icon={
                    isDownloading ? (
                      <Spinner size={14} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={!hasPayments || isDownloading}
                  data-testid="export-payments-button"
                />
              </span>
            </Tooltip>
          </>
        }
        data-testid="payments-header"
      />

      <PageToolbar
        actions={<PaymentGroupingMenu />}
        tabs={
          <PeriodSelector
            label={selectedPeriodLabel}
            shortLabel={selectedPeriodShortLabel}
            periodsByYear={periodsByYear}
            selection={{
              effectivePeriodId,
              onPeriodChange: handlePeriodChange,
            }}
            navigation={{
              onPrev: handlePrev,
              onNext: handleNext,
              canGoPrev,
              canGoNext,
            }}
            pagination={{
              hasMoreYears: periodData?.hasMoreYears,
              onLoadMoreYears: handleLoadMoreYears,
            }}
          />
        }
        data-testid="payments-toolbar"
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <PaymentsListView
          payments={payments}
          isLoading={isLoading}
          error={error}
          showBankDetails={showBankDetails}
          onPaymentClick={handlePaymentClick}
        />
      </div>

      <PageSnackbar snackbar={snackbar} onClose={handleCloseSnackbar} />
    </PageContentArea>
  );
}
